import { useMemo } from "react";
import { Table } from "antd";
import { cellKind, makeFormatter, makePercentFormatter } from "./reportingUtils";
import "./styles.scss";

// Nested column headers from the columns' paths: neighbouring columns with
// the same label at a level share one spanning header. leaf(col, index) makes
// the bottom header.
function headerTree(columns, leaf, level = 0, offset = 0) {
  const groups = [];
  columns.forEach((col, i) => {
    const index = offset + i;
    if (level >= col.path.length - 1) {
      groups.push(leaf(col, index));
      return;
    }
    const title = col.path[level];
    const last = groups[groups.length - 1];
    if (last?.group && last.title === title) last.members.push([col, index]);
    else groups.push({ group: true, title, key: `${level}:${index}:${title}`, members: [[col, index]] });
  });
  return groups.map((g) =>
    g.group
      ? {
          title: g.title,
          key: g.key,
          align: "center",
          // Members are consecutive, so their first index is the offset.
          children: headerTree(
            g.members.map(([c]) => c),
            leaf,
            level + 1,
            g.members[0][1]
          ),
        }
      : g
  );
}

// How many rows each label cell spans: a label merges with the rows below it
// while it and every label to its left stay the same. The last level never merges.
function labelSpans(rows, levels) {
  return Array.from({ length: levels }, (_, level) => {
    const spans = new Array(rows.length).fill(1);
    if (level === levels - 1) return spans;
    const same = (a, b) => a.labels.slice(0, level + 1).every((l, k) => l === b.labels[k]);
    let start = 0;
    for (let i = 1; i <= rows.length; i++) {
      if (i < rows.length && same(rows[i], rows[start])) continue;
      spans[start] = i - start;
      for (let k = start + 1; k < i; k++) spans[k] = 0;
      start = i;
    }
    return spans;
  });
}

// Rows with a style (applyRowStyles) get these classes, see styles.scss.
const rowClassName = (row) =>
  [
    row.style?.bold && "reporting-row-bold",
    row.style?.italic && "reporting-row-italic",
    row.style?.lineAbove && "reporting-row-line",
  ]
    .filter(Boolean)
    .join(" ");

const INDENT_PX = 16;

// table: from buildWidgetTable
export default function TableWidget({ table, fmt }) {
  // Amount formatter for a row: the table's, or one with the row's decimals
  // (built once per number of decimals).
  const formatValueFor = useMemo(() => {
    const byDecimals = new Map();
    const tableFormat = makeFormatter(fmt);
    return (row) => {
      const decimals = row.style?.decimals;
      if (decimals == null) return tableFormat;
      if (!byDecimals.has(decimals)) byDecimals.set(decimals, makeFormatter({ ...fmt, decimals }));
      return byDecimals.get(decimals);
    };
  }, [fmt]);
  const formatPercent = useMemo(() => makePercentFormatter(fmt), [fmt]);

  const dataSource = useMemo(() => {
    const spans = labelSpans(table.rows, table.rowTitles.length);
    return table.rows.map((row, i) => ({ ...row, spans: spans.map((s) => s[i]) }));
  }, [table]);

  const columns = useMemo(() => {
    const levels = table.rowTitles.length;
    const labelColumns = table.rowTitles.map((title, level) => ({
      title,
      key: `label:${level}`,
      fixed: "left",
      width: level === levels - 1 ? 240 : 180,
      ellipsis: true,
      // A non-breaking space keeps blank rows as tall as the others.
      render: (_, row) =>
        row.style?.indent && level === levels - 1 ? (
          <span style={{ paddingLeft: row.style.indent * INDENT_PX }}>{row.labels[level] || " "}</span>
        ) : (
          row.labels[level] || " "
        ),
      onCell: (row) => ({ rowSpan: row.spans[level] }),
    }));

    const leaf = (col, i) => ({
      title: col.path[col.path.length - 1],
      key: col.key,
      align: "right",
      onCell: (row) => ({ className: cellKind(row, col) === "value" ? undefined : "reporting-diff-col" }),
      render: (_, row) =>
        row.rowType
          ? ""
          : cellKind(row, col) === "pct"
            ? formatPercent(row.values[i])
            : formatValueFor(row)(row.values[i]),
    });

    return [...labelColumns, ...headerTree(table.columns, leaf)];
  }, [table, formatValueFor, formatPercent]);

  return (
    <Table
      className="reporting-table"
      size="small"
      bordered
      pagination={false}
      rowKey="key"
      rowClassName={rowClassName}
      columns={columns}
      dataSource={dataSource}
      scroll={{ x: "max-content" }}
      locale={{ emptyText: "Add a series to show data" }}
    />
  );
}
